import { useState, useCallback } from 'react';
import { useDeleteSessionMutation } from '~/api/chatbot/Chatbot.api';

export interface DeleteSessionResult {
    success: boolean;
    error?: string;
}

export interface UseDeleteSessionReturn {
    deleteSession: (sessionId: string) => Promise<DeleteSessionResult>;
    isDeleting: boolean;
    deleteError: string | null;
}

/**
 * Checks whether a response body contains an error flag, indicating
 * the deletion was not actually completed even if the HTTP status was 200.
 * Per Req 9.7: if both success and error signals are present, error takes priority.
 */
const hasErrorFlag = (response: unknown): boolean => {
    if (response && typeof response === 'object') {
        const data = response as Record<string, unknown>;
        return data.error === true || typeof data.error === 'string';
    }
    return false;
};

/**
 * Extracts an error message from a response body that contains an error flag.
 */
const extractErrorMessage = (response: unknown): string => {
    if (response && typeof response === 'object') {
        const data = response as Record<string, unknown>;
        if (typeof data.errorMessage === 'string') {
            return data.errorMessage;
        }
        if (typeof data.message === 'string') {
            return data.message;
        }
        if (typeof data.error === 'string') {
            return data.error;
        }
    }
    return 'Failed to delete session. Please try again.';
};

/**
 * Determines whether an error is an explicit API error response (not a timeout).
 * Per Req 9.6: only show errors for explicit API error responses, not timeouts.
 */
const isExplicitApiError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const err = error as Record<string, unknown>;

    // RTK Query timeout errors have status 'FETCH_ERROR' or name 'AbortError'
    if (err.status === 'FETCH_ERROR') {
        return false;
    }

    if (err.name === 'AbortError') {
        return false;
    }

    // Check for timeout-related error messages
    if (typeof err.error === 'string' && err.error.toLowerCase().includes('timeout')) {
        return false;
    }

    // Explicit API error responses have numeric HTTP status codes
    if (typeof err.status === 'number') {
        return true;
    }

    // If the error has data with an error property, it's an explicit API error
    if (err.data && typeof err.data === 'object') {
        return true;
    }

    return false;
};

/**
 * Custom hook for delete session logic with error prioritization.
 *
 * Handles the delete flow:
 * - Calls DELETE API endpoint
 * - On success (no error flag in response): returns { success: true }
 * - On error (explicit API error): returns { success: false, error: string }
 * - On conflicting signals (success + error flag): prioritizes error (Req 9.7)
 * - Network timeouts do not trigger error messages (Req 9.6)
 */
export const useDeleteSession = (): UseDeleteSessionReturn => {
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [deleteSessionMutation] = useDeleteSessionMutation();

    const deleteSession = useCallback(
        async (sessionId: string): Promise<DeleteSessionResult> => {
            setIsDeleting(true);
            setDeleteError(null);

            try {
                const response = await deleteSessionMutation(sessionId).unwrap();

                // Req 9.7: If response contains both success and error signals,
                // prioritize error and retain the session
                if (hasErrorFlag(response)) {
                    const errorMessage = extractErrorMessage(response);
                    setDeleteError(errorMessage);
                    return { success: false, error: errorMessage };
                }

                // Req 9.4, 9.5: Clean success — session should be removed
                return { success: true };
            } catch (err: unknown) {
                // Req 9.6: Only show errors for explicit API error responses, not timeouts
                if (isExplicitApiError(err)) {
                    const errorMessage = extractErrorMessage(
                        (err as Record<string, unknown>).data ?? err
                    );
                    setDeleteError(errorMessage);
                    return { success: false, error: errorMessage };
                }

                // Timeout or network error — do not display an error message
                return { success: false };
            } finally {
                setIsDeleting(false);
            }
        },
        [deleteSessionMutation]
    );

    return {
        deleteSession,
        isDeleting,
        deleteError,
    };
};
